import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaCheckCircle, FaExclamationCircle, FaSpinner } from 'react-icons/fa';
import logoArrows from '../images/logo_arrows.svg';
import { useTranslation } from '../contexts/LanguageContext';

const VerifyEmail = () => {
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('');
  
  const navigate = useNavigate();
  const { token } = useParams();
  const { t } = useTranslation();

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage(t('verifyEmail.errors.missingToken'));
      return;
    }

    const verifyEmailToken = async () => {
      try {
        const response = await fetch(
          `https://easysubs-1ad308994ada.herokuapp.com/api/verify-email?token=${encodeURIComponent(token)}`
        );

        const data = await response.json();

        if (response.ok) {
          setStatus('success');
          setMessage(t('verifyEmail.success.message'));
        } else {
          setStatus('error');
          setMessage(data.error || t('verifyEmail.errors.verificationFailed'));
        }
      } catch (error) {
        setStatus('error');
        setMessage(t('verifyEmail.errors.networkError'));
      }
    };

    // Use setTimeout to ensure this runs after component mount
    const timer = setTimeout(() => {
      verifyEmailToken();
    }, 100);

    return () => clearTimeout(timer);
  }, []); // Empty dependency array - runs once on mount

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-gray-100 flex flex-col items-center justify-center px-4 py-8">
      <div className="w-full max-w-md bg-white rounded-xl shadow-xl overflow-hidden border border-gray-200">
        <div className="bg-gradient-to-r from-[#253985] to-[#1a2a6c] text-white py-8 px-6 text-center">
          <div className="flex justify-center mb-4">
            <img src={logoArrows} alt="EasySubs Logo" className="h-16" />
          </div>
          <h1 className="text-2xl font-bold">{t('verifyEmail.title')}</h1>
        </div>
        
        <div className="p-6">
          {status === 'loading' && (
            <div className="flex flex-col items-center py-8">
              <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center">
                <FaSpinner className="text-3xl text-blue-600 animate-spin" />
              </div>
              <p className="text-gray-700 text-center mt-4">{t('verifyEmail.loading')}</p>
            </div>
          )}
          
          {status === 'success' && (
            <div className="flex flex-col items-center py-8">
              <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-4">
                <FaCheckCircle className="text-4xl text-green-500" />
              </div>
              <h2 className="text-xl font-bold text-gray-800 mb-2">
                {t('verifyEmail.success.title')}
              </h2>
              <p className="text-gray-600 text-center mb-6">
                {message}
              </p>
              <button
                onClick={() => navigate('/demo')}
                className="w-full py-3 px-4 rounded-xl font-medium text-white bg-gradient-to-r from-green-500 to-green-700"
              >
                {t('verifyEmail.success.button')}
              </button>
            </div>
          )}
          
          {status === 'error' && (
            <div className="flex flex-col items-center py-8">
              <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mb-4">
                <FaExclamationCircle className="text-4xl text-red-500" />
              </div>
              <h2 className="text-xl font-bold text-gray-800 mb-2">
                {t('verifyEmail.error.title')}
              </h2>
              <p className="text-gray-600 text-center mb-6">
                {message}
              </p>
              <button
                onClick={() => navigate('/')}
                className="text-blue-600 hover:text-blue-800 font-medium py-2 underline"
              >
                {t('verifyEmail.error.button')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;